import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { apiStateSelectors } from '../../api/apiState'
import { accountingApiEvents, accountingApiSelectors } from '../../api/accounting'
import { agencyApiSelectors } from '../../api/agency'
import UpdateInvoiceNumberPanel from './UpdateInvoiceNumberPanel/UpdateInvoiceNumberPanel'
import DiscountPanel, { DiscountTarget } from './DiscountPanel/DiscountPanel'
import AddAgencyPanel, { AgencyOption } from './AddAgencyPanel/AddAgencyPanel'
import styles from './AdminBillingPanel.module.scss'

// Which panel the drawer is showing. Null is closed.
export type PanelMode = { kind: 'counter' } | { kind: 'add' } | { kind: 'discounts'; target: DiscountTarget } | null

type TProps = {
  mode: PanelMode
  onClose: () => void
  onPickAgency: (agency: AgencyOption) => void
}

// Its own right-hand drawer rather than LayoutSecondaryPanel: both shared
// layout panels are left-anchored and other pages rely on that.
const AdminBillingPanel = ({ mode, onClose, onPickAgency }: TProps) => {
  const dispatch = useDispatch()

  const settings = useSelector(accountingApiSelectors.getBillingAgencySettings)
  const agencyListing = useSelector(agencyApiSelectors.getAgencyListing)
  const isSaving = useSelector(state =>
    apiStateSelectors.isLoading(state)([
      accountingApiEvents.updateBillingInvoiceNumber_request,
      accountingApiEvents.setBillingAgencyDiscounts_request,
      accountingApiEvents.removeBillingAgencySettings_request,
    ]),
  )

  // Only agencies without a settings row are worth adding; the rest are
  // edited from the table.
  const addable = useMemo<AgencyOption[]>(() => {
    const configured = new Set((settings?.settings ?? []).map(s => s.agencyId))
    return ((agencyListing as AgencyOption[]) ?? [])
      .filter(a => a.agencyId && !configured.has(a.agencyId))
      .sort((a, b) => (a.agencyName ?? '').localeCompare(b.agencyName ?? ''))
  }, [agencyListing, settings])

  useEffect(() => {
    if (!mode) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mode, onClose])

  if (!mode) return null

  // Closes on the request, not the response — the epics re-read the settings,
  // so the page behind is the confirmation.
  const dispatchAndClose = (action: { type: string; payload?: unknown }) => {
    dispatch(action)
    onClose()
  }

  return (
    <aside className={styles.drawer} aria-label="Billing settings">
      {mode.kind === 'counter' && (
        <UpdateInvoiceNumberPanel
          lastUsed={settings?.lastBillingInvoiceNumber ?? null}
          saving={isSaving}
          onCancel={onClose}
          onSave={value => dispatchAndClose(accountingApiEvents.updateBillingInvoiceNumber_request({ number: value }))}
        />
      )}

      {mode.kind === 'add' && <AddAgencyPanel options={addable} onCancel={onClose} onPick={onPickAgency} />}

      {mode.kind === 'discounts' && (
        <DiscountPanel
          target={mode.target}
          saving={isSaving}
          onCancel={onClose}
          onSave={({ agencyId, processing, transaction, notes }) =>
            dispatchAndClose(
              accountingApiEvents.setBillingAgencyDiscounts_request({
                agencyId,
                percentProcessingFeeDiscount: processing,
                percentTransactionFeeDiscount: transaction,
                notes,
              }),
            )
          }
          onRemove={agencyId => dispatchAndClose(accountingApiEvents.removeBillingAgencySettings_request({ agencyId }))}
        />
      )}
    </aside>
  )
}

export default AdminBillingPanel
